import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaCity, FaSearch, FaPlay, FaClock, FaStar } from 'react-icons/fa';

interface TehranLesson {
  _id: string;
  title: string;
  description: string;
  category: string;
  difficulty: string;
  order: number;
  dialogue: Array<{
    person: string;
    farsi: string;
    english: string;
  }>;
  vocabulary: Array<{
    farsi: string;
    english: string;
  }>;
  culturalNotes: string[];
}

const TehranLessons: React.FC = () => {
  const [lessons, setLessons] = useState<TehranLesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');

  const categories = [
    { value: '', label: 'All Categories' },
    { value: 'breakfast', label: 'Breakfast' },
    { value: 'shopping', label: 'Shopping' },
    { value: 'transport', label: 'Transport' },
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'social', label: 'Social' },
    { value: 'health', label: 'Health' }
  ];

  const difficulties = [
    { value: '', label: 'All Levels' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  const fetchLessons = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedDifficulty) params.append('difficulty', selectedDifficulty);
      
      const response = await fetch(`/api/cultural/tehran?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        setLessons(data.data);
      }
    } catch (error) {
      console.error('Error fetching Tehran lessons:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedDifficulty]);

  useEffect(() => {
    fetchLessons();
  }, [fetchLessons]);

  const filteredLessons = lessons.filter(lesson =>
    lesson.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lesson.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-50 text-green-700';
      case 'intermediate': return 'bg-yellow-50 text-yellow-700';
      case 'advanced': return 'bg-red-50 text-red-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'breakfast': return 'bg-orange-50 text-orange-700';
      case 'shopping': return 'bg-blue-50 text-blue-700';
      case 'transport': return 'bg-purple-50 text-purple-700';
      case 'restaurant': return 'bg-green-50 text-green-700';
      case 'social': return 'bg-pink-50 text-pink-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <FaCity className="text-4xl text-blue-600 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800">
              A Day in Tehran
            </h1>
          </div>
          <p className="text-xl text-gray-700 mb-2">
            Experience daily life in Tehran through interactive dialogues
          </p>
          <p className="text-lg text-gray-600">
            Learn practical Persian conversations for real-life situations
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search lessons..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
              >
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Difficulty Filter */}
            <div>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
              >
                {difficulties.map((difficulty) => (
                  <option key={difficulty.value} value={difficulty.value}>
                    {difficulty.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Lessons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLessons.map((lesson) => (
            <Link
              key={lesson._id}
              to={`/cultural/tehran/${lesson._id}`}
              className="group block"
            >
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                      {lesson.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {lesson.description}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                    <FaPlay className="text-sm" />
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(lesson.category)}`}>
                    {lesson.category}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${getDifficultyColor(lesson.difficulty)}`}>
                    {lesson.difficulty}
                  </span>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center">
                    <FaClock className="mr-1" />
                    <span>{lesson.dialogue.length} dialogues</span>
                  </div>
                  <div className="flex items-center">
                    <FaStar className="mr-1" />
                    <span>{lesson.vocabulary.length} words</span>
                  </div>
                </div>

                {/* Preview Dialogue */}
                {lesson.dialogue.length > 0 && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm">
                      <div className="font-semibold text-gray-800 mb-1">
                        {lesson.dialogue[0].person}:
                      </div>
                      <div className="text-gray-600 mb-1">
                        {lesson.dialogue[0].farsi}
                      </div>
                      <div className="text-gray-500 text-xs italic">
                        "{lesson.dialogue[0].english}"
                      </div>
                    </div>
                  </div>
                )}

                {/* Cultural Notes Preview */}
                {lesson.culturalNotes.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-500 mb-1">
                      Cultural Note:
                    </div>
                    <div className="text-xs text-gray-600 line-clamp-1">
                      {lesson.culturalNotes[0]}
                    </div>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>

        {/* Empty State */}
        {filteredLessons.length === 0 && (
          <div className="text-center py-12">
            <FaCity className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No lessons found
            </h3>
            <p className="text-gray-500">
              Try adjusting your search or filters
            </p>
          </div>
        )}

        {/* Learning Tips */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white shadow-lg">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Learning Tips for Tehran Lessons</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <h3 className="font-semibold mb-2">Practice Dialogues</h3>
                <p className="text-blue-50">Read the dialogues aloud to improve pronunciation</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Learn Vocabulary</h3>
                <p className="text-blue-50">Study the key words before practicing conversations</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Cultural Context</h3>
                <p className="text-blue-50">Pay attention to cultural notes for deeper understanding</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TehranLessons; 